import React from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { AlertTriangle, RefreshCw, Home, ArrowLeft } from 'lucide-react';

export function RouteErrorBoundary() {
  const error = useRouteError();

  // Generate error ID for tracking
  const errorId = `ROUTE-ERR-${Date.now().toString(36)}-${Math.random().toString(36).substr(2, 5)}`.toUpperCase();

  // Determine error type and message
  let errorMessage = 'An unexpected error occurred';
  let errorStatus = 500;

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
    errorMessage = error.statusText || error.data?.message || `${error.status} Error`;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  // Log error details
  React.useEffect(() => {
    const errorDetails = {
      message: errorMessage,
      status: errorStatus,
      errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      error: error
    };

    console.group('🚨 Router Error Caught');
    console.error('Route Error:', error);
    console.error('Error Details:', errorDetails);
    console.groupEnd();

    // Store in localStorage for debugging
    try {
      const storedErrors = JSON.parse(localStorage.getItem('route_errors') || '[]');
      storedErrors.push(errorDetails);
      localStorage.setItem('route_errors', JSON.stringify(storedErrors.slice(-5)));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [error, errorMessage, errorStatus, errorId]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleGoBack = () => {
    window.history.back();
  };

  // Custom messages for common HTTP errors
  const getErrorTitle = () => {
    switch (errorStatus) {
      case 404:
        return "Page Not Found";
      case 403:
        return "Access Denied";
      case 401:
        return "Authentication Required";
      case 500:
        return "Server Error";
      default:
        return "Something went wrong";
    }
  };

  const getErrorDescription = () => {
    switch (errorStatus) {
      case 404:
        return "The page you're looking for doesn't exist. It may have been moved or deleted.";
      case 403:
        return "You don't have permission to access this page. Please check your account permissions.";
      case 401:
        return "You need to be signed in to access this page.";
      case 500:
        return "We're experiencing technical difficulties. Our team has been notified.";
      default:
        return "An unexpected error occurred. This has been logged and we'll look into it.";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-error-100 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-error-600" />
          </div>
          <CardTitle className="text-xl">{getErrorTitle()}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant={errorStatus >= 400 && errorStatus < 500 ? "warning" : "error"}>
            <AlertTriangle className="w-4 h-4" />
            <div>
              <p className="font-medium">{errorMessage}</p>
              <p className="text-sm mt-1">
                {getErrorDescription()}
              </p>
            </div>
          </Alert>

          {errorStatus >= 500 && (
            <div className="bg-gray-100 rounded-md p-3 text-sm">
              <p className="text-gray-600">
                <strong>Error ID:</strong> {errorId}
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Please include this ID if you contact support.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-2">
            {errorStatus === 401 ? (
              <Button 
                onClick={() => window.location.href = '/auth'}
                className="w-full"
              >
                Sign In
              </Button>
            ) : (
              <Button 
                onClick={handleRefresh}
                leftIcon={<RefreshCw className="w-4 h-4" />}
                className="w-full"
              >
                Refresh Page
              </Button>
            )}
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                onClick={handleGoBack}
                leftIcon={<ArrowLeft className="w-4 h-4" />}
                className="flex-1"
              >
                Go Back
              </Button>
              <Button 
                variant="ghost" 
                onClick={handleGoHome}
                leftIcon={<Home className="w-4 h-4" />}
                className="flex-1"
              >
                Go Home
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}